import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import { GiftCardPayload } from '@/types/GiftCard';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

export const checkoutApi = createApi({
  reducerPath: 'checkoutApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `shopping` }),
  tagTypes: ['ShoppingCart'],

  endpoints: builder => ({
    applyCoupon: builder.mutation<SuccessResponse<unknown>, { couponCode: string }>({
      query: data => ({
        url: `/UseCouponCode`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['ShoppingCart'],
    }),

    applyGiftCard: builder.mutation<SuccessResponse<unknown>, GiftCardPayload>({
      query: data => ({
        url: `/UseGiftCard`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['ShoppingCart'],
    }),
    removeCoupon: builder.mutation<SuccessResponse<unknown>, undefined>({
      query: coupon => ({ url: `/RemoveCouponCode`, method: 'POST', body: JSON.stringify({ coupon }) }),
      transformResponse: (response: { data: SuccessResponse<unknown> }, meta, arg) => response.data,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['ShoppingCart'],
    }),
    removeGiftCard: builder.mutation<SuccessResponse<unknown>, undefined>({
      query: payload => ({ url: `/RemoveGiftCard`, method: 'POST', body: JSON.stringify(payload) }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['ShoppingCart'],
    }),

    checkout: builder.mutation<{ success: boolean; message: string }, void>({
      query: payload => ({ url: `/`, method: 'POST', body: JSON.stringify(payload) }),
      transformResponse: (response: { data: { success: boolean; message: string } }, meta, arg) => response.data,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useApplyCouponMutation,
  useApplyGiftCardMutation,
  useCheckoutMutation,
  useRemoveCouponMutation,
  useRemoveGiftCardMutation,
} = checkoutApi;
